import React, { useContext, useState, useEffect } from "react";
import { ProjectContext } from "../../context/project_context";
import debounce from 'lodash/debounce';
import { countDuplicateEnglishTranscripts } from "../../utils/english_transcript_duplicates";

const ProjectGenerateTTS = () => {
  const {
    project,
    org_id,
    project_id,
    updateProject,
    fetchEnglishTranscripts,
    english_transcripts,
    generateEnglishTTS,
    generateSingleEnglishTTS,
    handleSaveCorrectEnglishSentenceTranscript,
    setEnglishTranscripts,
    voices,
    selectedVoiceId,
    setSelectedVoiceId,
    selectedElevenLabsApi,
    handleElevenLabsApiChange,
    removeDuplicateEnglishTranscripts
  } = useContext(ProjectContext);
  
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTexts, setEditedTexts] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [ttsGeneratingStates, setTtsGeneratingStates] = useState({});
  const [audioRefreshKeys, setAudioRefreshKeys] = useState({});
  const [debouncedFunctions] = useState(() => ({}));

  useEffect(() => {
    if (project?.id) {
      fetchEnglishTranscripts();
    }
  }, [project?.id]);

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "correct_english"
    });
  };

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "completed"
    });
  };

  const handleMoveToPerLanguage = async () => {
    await updateProject({
      ...project,
      project_status: "per_language"
    });
  };

  const handleGenerateTTS = async () => {
    setLoading(true);
    try {
      await generateEnglishTTS(selectedVoiceId);
      await fetchEnglishTranscripts();
    } catch (error) {
      console.error('Error generating TTS:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (transcriptId, newText) => {
    setEditedTexts(prev => ({
      ...prev,
      [transcriptId]: newText
    }));

    // Cancel previous debounced call if it exists
    if (debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId].cancel();
    }

    // Create new debounced function if it doesn't exist
    if (!debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId] = debounce(async (text) => {
        try {
          setSavingStates(prev => ({ ...prev, [transcriptId]: true }));
          await handleSaveCorrectEnglishSentenceTranscript(transcriptId, text);
          
          // Update local state
          setEnglishTranscripts(prev => 
            prev.map(t => t.id === transcriptId ? { ...t, final_text: text } : t)
          );
          
          setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
        } catch (error) {
          console.error('Failed to save text:', error);
          setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
        }
      }, 1000);
    }

    // Call the debounced function
    debouncedFunctions[transcriptId](newText);
  };

  const handleGenerateSingleTTS = async (transcriptId) => {
    try {
      setTtsGeneratingStates(prev => ({ ...prev, [transcriptId]: true }));
      await generateSingleEnglishTTS(transcriptId, selectedVoiceId);
      // Update refresh key to force audio player to reload
      setAudioRefreshKeys(prev => ({ ...prev, [transcriptId]: Date.now() }));
      setTtsGeneratingStates(prev => ({ ...prev, [transcriptId]: false }));
    } catch (error) {
      console.error('Failed to generate TTS:', error);
      alert('Failed to generate TTS: ' + error.message);
      setTtsGeneratingStates(prev => ({ ...prev, [transcriptId]: false }));
    }
  };

  const handleKeyDown = (e, currentIndex) => {
    const textareas = document.querySelectorAll('.english-text-textarea');
    
    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  const duplicateCount = countDuplicateEnglishTranscripts(english_transcripts);

  const handleRemoveDuplicates = async () => {
    if (duplicateCount === 0) return;
    if (!window.confirm(
      `Remove ${duplicateCount} duplicate row(s)? Rows with the same timeframe and original text will be deleted. The earliest row in each group will be kept.`
    )) return;
    await removeDuplicateEnglishTranscripts();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="generate-tts">
      <div className="d-flex justify-content-between align-items-center">
        <h2>Generate Text-to-Speech</h2>
        <div className="d-flex gap-2 mt-3 align-items-center">
          {/* Edit Mode Toggle */}
          <button 
            className={`btn ${editMode ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
          </button>
          {/* ElevenLabs API Selector */}
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '160px' }}
            value={selectedElevenLabsApi}
            onChange={(e) => handleElevenLabsApiChange(e.target.value)}
            title="ElevenLabs TTS model"
          >
            <option value="multilingual_v2">Multilingual v2</option>
            <option value="eleven_v3">Eleven v3</option>
          </select>
          {/* Voice Selector */}
          <select 
            className="form-select" 
            style={{ width: 'auto', minWidth: '180px' }}
            value={selectedVoiceId}
            onChange={(e) => setSelectedVoiceId(e.target.value)}
          >
            <option value="">Default Voice</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.voice_id}>
                {voice.name}
              </option>
            ))}
          </select>
          {/* Trigger generate tts */}
          <button className="btn btn-primary" onClick={handleGenerateTTS}>
            Generate All TTS
          </button>
          <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/english_srt_text`} className="btn btn-primary" target="_blank" download={`${project.name}_english_srt.srt`}>
            Download English SRT
          </a>
          <div className="dropdown">
            <button className="btn btn-primary dropdown-toggle" type="button" id="downloadDropdown" data-bs-toggle="dropdown" aria-expanded="false">
              Download Audio Package
            </button>
            <ul className="dropdown-menu" aria-labelledby="downloadDropdown">
              <li>
                <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_english_package?type=individual`} className="dropdown-item" target="_blank" download>
                  Individual Audio Files
                </a>
              </li>
              <li>
                <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_english_package?type=combined`} className="dropdown-item" target="_blank" download>
                  Combined Audio File
                </a>
              </li>
              <li>
                <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_english_package?type=complete`} className="dropdown-item" target="_blank" download>
                  Complete Package
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="transcripts-list mt-3">
        <div className="d-flex gap-2 mb-2">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleRemoveDuplicates}
            disabled={duplicateCount === 0}
          >
            Remove Duplicates ({duplicateCount})
          </button>
        </div>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>T#</th>
              <th style={{ width: '200px' }}>Timeframe</th>
              <th>Taglish Text</th>
              <th>English Text</th>
              <th style={{ width: editMode ? '200px' : '150px' }}>TTS Status</th>
            </tr>
          </thead>
          <tbody>
            {english_transcripts.map((transcript, index) => {
              const currentText = editedTexts[transcript.id] !== undefined 
                ? editedTexts[transcript.id] 
                : (transcript.final_text || '');
              const audioUrl = transcript.mp3_url 
                ? `${transcript.mp3_url}${transcript.mp3_url.includes('?') ? '&' : '?'}t=${audioRefreshKeys[transcript.id] || ''}`
                : null;
              
              return (
                <tr key={transcript.id}>
                  <td>{transcript.sequence_number}</td>
                  <td>{transcript.timeframe}</td>
                  <td>{transcript.original_text}</td>
                  <td className={editMode ? "p-1" : ""}>
                    {editMode ? (
                      <>
                        <textarea
                          className="form-control english-text-textarea border-0 rounded-0"
                          style={{ minHeight: '60px', maxHeight: '100px', resize: 'vertical' }}
                          value={currentText}
                          onChange={(e) => handleTextChange(transcript.id, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          placeholder="Enter English text..."
                        />
                        {savingStates[transcript.id] && (
                          <small className="text-muted">Saving...</small>
                        )}
                      </>
                    ) : (
                      transcript.final_text || <span className="text-muted">No text</span>
                    )}
                  </td>
                  <td>
                    {editMode ? (
                      <div className="d-flex flex-column gap-1">
                        {transcript.mp3_generated && transcript.mp3_url ? (
                          <>
                            <audio 
                              key={`audio-${transcript.id}-${audioRefreshKeys[transcript.id] || 0}`}
                              controls 
                              className="w-100"
                              style={{ maxWidth: '180px', height: '32px' }}
                            >
                              <source src={audioUrl} type="audio/mpeg" />
                              Your browser does not support the audio element.
                            </audio>
                            <a 
                              href={audioUrl} 
                              className="btn btn-sm btn-success"
                              target="_blank"
                              download={`${project.id}_english_tts_${transcript.sequence_number}.mp3`}
                            >
                              Download
                            </a>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleGenerateSingleTTS(transcript.id)}
                              disabled={ttsGeneratingStates[transcript.id] || !currentText}
                              title={!currentText ? 'English text required' : 'Regenerate TTS'}
                            >
                              {ttsGeneratingStates[transcript.id] ? 'Generating...' : 'Regenerate'}
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleGenerateSingleTTS(transcript.id)}
                            disabled={ttsGeneratingStates[transcript.id] || !currentText}
                            title={!currentText ? 'English text required' : 'Generate TTS'}
                          >
                            {ttsGeneratingStates[transcript.id] ? 'Generating...' : 'Generate TTS'}
                          </button>
                        )}
                        {!currentText && (
                          <small className="text-muted">Text required</small>
                        )}
                      </div>
                    ) : (
                      <>
                        {transcript.mp3_generated ? (
                          <a 
                            href={audioUrl} 
                            className="text-success" 
                            target="_blank" 
                            download={`${project.id}_english_tts_${transcript.sequence_number}.mp3`}
                          >
                            Download
                          </a>
                        ) : (
                          <span className="text-muted">...</span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back to English Correction
        </button>
        <button
          className="btn btn-primary"
          onClick={handleMoveToPerLanguage}
          disabled={loading}
        >
          Move to Per Language
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={loading}
        >
          Complete TTS Generation
        </button>
      </div>
    </div>
  );
};

export default ProjectGenerateTTS;